import api from "../../../api/api";
import type { Item } from "../ReportServices";

export interface UDFCategory {
  displayName: string;
  items: Item[];
}

export const loadUdfs = async (
  call: string,
  authToken: string,
  identityProvider: string
): Promise<Item[]> => {
  if (!call || !call.includes("&udf=*")) {
    return [];
  }

  let cleanCall = call;
  let i = cleanCall.indexOf("&");
  if (i >= 0) {
    cleanCall = cleanCall.substring(0, i);
  }

  try {
    const response = await api.fetch(
      `udfs/${cleanCall}`,
      authToken,
      identityProvider
    );
    if (!response.ok) throw new Error("Response not OK");
    const data: any = await response.json();
    let rowItems = data.result.map((row: any) => ({
      id: row.key,
      name: row.name,
      displayName: row.displayName,
    }));
    rowItems = Array.from(
      new Map(rowItems.map((obj: Item) => [obj.id, obj])).values()
    );
    return rowItems;
  } catch (error) {
    throw new Error("Error fetching UDFs: " + error);
  }
};

export const groupUDFsByCategory = (udfs: Item[]): UDFCategory[] => {
  const grouped = new Map<string, Item[]>();

  udfs.forEach((udf) => {
    const category = udf.displayName || "Other";
    if (!grouped.has(category)) {
      grouped.set(category, []);
    }
    grouped.get(category)!.push(udf);
  });

  return Array.from(grouped.entries()).map(([displayName, items]) => ({
    displayName,
    items: items.sort((a, b) => a.name.localeCompare(b.name)),
  }));
};

export const getSelectedUdfsByCategory = (
  selectedUdfIds: string,
  categories: UDFCategory[]
): Record<string, boolean> => {
  const selectedIds = new Set(selectedUdfIds ? selectedUdfIds.split(", ") : []);
  const categoryStates: Record<string, boolean> = {};

  categories.forEach((category) => {
    const allSelected = category.items.every((item) => selectedIds.has(item.id));
    categoryStates[category.displayName] = allSelected && category.items.length > 0;
  });

  return categoryStates;
};

export const handleUdfCategorySelect = (
  categoryName: string,
  isSelected: boolean,
  categories: UDFCategory[],
  currentSelectedIds: string
): string => {
  const selectedIds = new Set(currentSelectedIds ? currentSelectedIds.split(", ") : []);
  const category = categories.find((c) => c.displayName === categoryName);

  if (!category) return currentSelectedIds;

  category.items.forEach((item) => {
    if (isSelected) {
      selectedIds.add(item.id);
    } else {
      selectedIds.delete(item.id);
    }
  });

  return Array.from(selectedIds).join(", ");
};

export const handleUdfItemSelect = (
  itemId: string,
  isSelected: boolean,
  currentSelectedIds: string
): string => {
  const selectedIds = new Set(currentSelectedIds ? currentSelectedIds.split(", ") : []);

  if (isSelected) {
    selectedIds.add(itemId);
  } else {
    selectedIds.delete(itemId);
  }

  return Array.from(selectedIds).join(", ");
};

export const isCategoryPartiallySelected = (
  category: UDFCategory,
  selectedUdfIds: string
): boolean => {
  const selectedIds = new Set(selectedUdfIds ? selectedUdfIds.split(", ") : []);
  const selectedCount = category.items.filter((item) => selectedIds.has(item.id)).length;
  return selectedCount > 0 && selectedCount < category.items.length;
};

export const handleMasterUdfSelect = (
  isSelected: boolean,
  categories: UDFCategory[]
): string => {
  if (!isSelected) {
    return "";
  }

  const allIds: string[] = [];
  categories.forEach((category) => {
    category.items.forEach((item) => {
      allIds.push(item.id);
    });
  });

  return allIds.join(", ");
};

export const getTotalSelectedUdfCount = (
  selectedUdfIds: string,
  categories: UDFCategory[]
): { selected: number; total: number } => {
  const selectedIds = new Set(selectedUdfIds ? selectedUdfIds.split(", ") : []);
  let total = 0;
  let selected = 0;

  categories.forEach((category) => {
    category.items.forEach((item) => {
      total++;
      if (selectedIds.has(item.id)) {
        selected++;
      }
    });
  });

  return { selected, total };
};

export const isMasterSelectPartiallySelected = (
  selectedUdfIds: string,
  categories: UDFCategory[]
): boolean => {
  const { selected, total } = getTotalSelectedUdfCount(selectedUdfIds, categories);
  return selected > 0 && selected < total;
};
